import { useEffect, useRef, useState } from "react";

import { Column, Row } from "@/components";
import { NotificationPopupCard } from "@/components/notifications/NotificationPopupCard";
import { PauseRequestCard } from "@/components/pause-requests/PauseRequestCard";
import { ScreeningInProgressContainer } from "@/components/screening-in-progress-modal/ScreeningInProgressContainer";
import { Badge } from "@/components/ui/badge";

import { useGetActiveScreens } from "@/hooks/queries/useGetActiveScreens";
import { useGetPendingPauseRequests } from "@/hooks/queries/useGetPendingPauseRequests";
import { useGetPopupNotifications } from "@/hooks/queries/useGetPopupNotifications";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import {
  CORNER_POPUP_WIDTH_CLASS,
  MS_PER_SECOND,
  PERSISTENT_POPUP_NOTIFICATION_TYPES,
  VANISHING_POPUP_DURATION_MS,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_NOTIFICATION_SURFACES,
  MIXPANEL_SCREENS,
  MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS,
} from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  ActiveScreeningPrompt,
  NotificationResponse,
  NotificationType,
  PendingPauseRequest,
} from "@/types";

// Keep in sync with the card's animate-out duration so the exit finishes before the cache drops it
const POPUP_EXIT_DURATION_MS = 300;

// How often the pile checks which vanishing cards have run out their time
const VANISH_SWEEP_INTERVAL_MS = 5000;

// One slot in the pile, holding whichever of the three prompt kinds it came
// from.
interface QueuedPrompt {
  at: string;
  notification?: NotificationResponse;
  pauseRequest?: PendingPauseRequest;
  screen?: ActiveScreeningPrompt;
}

// The bottom-left pile: one card at a time with everything else behind the
// overflow badge, screens ahead of notifications. Both surfaces live here so the
// count and "Dismiss all" cover the whole pile rather than one source.
export const CornerPromptStack = () => {
  /* Database CRUD Operations */
  // Read
  // Call requests are the one popup type that doesn't belong in this pile —
  // they get the top banner, where a two-minute countdown can't sit behind an
  // overflow badge.
  // An excellent candidate is the one card worth seeing first, so it fronts the
  // pile instead of waiting behind whatever landed most recently.
  const { data: allNotifications = [] } = useGetPopupNotifications();
  const notifications = allNotifications.filter(
    (notification) =>
      notification.type !== NotificationType.CANDIDATE_REQUESTED_CALL,
  );
  const { data: activeScreens = [] } = useGetActiveScreens();
  // Polled like the screens rather than notified: a request is a live entity
  // that leaves the pile when it stops being PENDING, decided one way or other.
  const { data: pauseRequests = [] } = useGetPendingPauseRequests();
  // Update
  const { mutate: updateNotifications } = useUpdateSetNotifications();

  /* React Hooks */
  // Screen dismissals are deliberately session-only — they come back on refresh.
  const [dismissedInterviewIds, setDismissedInterviewIds] = useState<
    Set<number>
  >(new Set());
  const [dismissingNotificationId, setDismissingNotificationId] = useState<
    number | null
  >(null);
  // Rotating rather than dropping keeps every card in the pile reachable.
  const [rotation, setRotation] = useState(0);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When the card currently on top runs out of time. Null while the top card is
  // one that persists, or while it is already on its way out.
  const vanishDeadlineAtRef = useRef<number | null>(null);
  const hoveredNotificationIdRef = useRef<number | null>(null);
  const expireFrontRef = useRef<() => void>(() => undefined);

  useEffect(
    () => () => {
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    },
    [],
  );

  /* Helpers */
  const screens = activeScreens.filter(
    (screen) => !dismissedInterviewIds.has(screen.interviewId),
  );
  const isVanishing = (notification: NotificationResponse) =>
    !PERSISTENT_POPUP_NOTIFICATION_TYPES.includes(notification.type);
  // Screens and notifications share one queue, newest on top, so whatever just
  // landed is the card the staffer sees.
  const queue: QueuedPrompt[] = [
    ...screens.map((screen) => ({ screen, at: screen.dateTime })),
    ...pauseRequests.map((pauseRequest) => ({
      pauseRequest,
      at: pauseRequest.createdAt,
    })),
    ...notifications.map((notification) => ({
      notification,
      at: notification.createdAt,
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  const totalCount = queue.length;
  const frontIndex = totalCount ? rotation % totalCount : 0;
  const frontScreen = queue[frontIndex]?.screen;
  const frontNotification = queue[frontIndex]?.notification;
  const frontPauseRequest = queue[frontIndex]?.pauseRequest;
  const overflowCount = totalCount ? totalCount - 1 : 0;
  const canDismissAll = totalCount > 1;

  /* Handlers */
  const secondsSinceCreated = (notification: NotificationResponse) =>
    Math.round(
      (Date.now() - new Date(notification.createdAt).getTime()) / MS_PER_SECOND,
    );

  const trackVanishingDismissal = (
    notification: NotificationResponse,
    dismissal: MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS,
  ) => {
    if (!isVanishing(notification)) return;
    mixpanelUtils.trackVanishingNotificationDismissal({
      notification_type: notification.type,
      dismissal,
      seconds_since_created: secondsSinceCreated(notification),
    });
  };

  // Plays the card out, then writes the dismissal. Shared by the X and by a
  // vanishing card running out of time, which report themselves separately.
  const startExit = (notificationId: number) => {
    if (dismissingNotificationId !== null) return;
    setDismissingNotificationId(notificationId);
    exitTimeoutRef.current = setTimeout(() => {
      exitTimeoutRef.current = null;
      setDismissingNotificationId(null);
      updateNotifications({ notificationIds: [notificationId], dismiss: true });
    }, POPUP_EXIT_DURATION_MS);
  };

  const handleDismissNotification = (notificationId: number) => {
    if (dismissingNotificationId !== null) return;
    const notification = notifications.find(({ id }) => id === notificationId);
    if (notification) {
      trackVanishingDismissal(
        notification,
        MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS.ACTIVE,
      );
    }
    startExit(notificationId);
  };

  // Only the card on top runs a clock — it vanishes after 15 seconds, and
  // whatever rotates up behind it starts its own.
  const expireFrontNotification = () => {
    const deadlineAt = vanishDeadlineAtRef.current;
    if (deadlineAt === null || !frontNotification) return;

    // Hovering holds the card in place rather than counting against it
    if (hoveredNotificationIdRef.current === frontNotification.id) {
      vanishDeadlineAtRef.current = deadlineAt + VANISH_SWEEP_INTERVAL_MS;
      return;
    }

    if (Date.now() < deadlineAt) return;

    vanishDeadlineAtRef.current = null;
    trackVanishingDismissal(
      frontNotification,
      MIXPANEL_VANISHING_NOTIFICATION_DISMISSALS.PASSIVE,
    );
    startExit(frontNotification.id);
  };

  // The sweep runs off a ref so the interval is set up once but always sees the
  // current pile.
  useEffect(() => {
    expireFrontRef.current = expireFrontNotification;
  });

  // Reaching the top starts the clock. A card can also be swapped out from
  // under the cursor without a mouseleave firing, so the hover hold is released
  // here rather than waiting for one.
  useEffect(() => {
    hoveredNotificationIdRef.current = null;
    vanishDeadlineAtRef.current =
      frontNotification && isVanishing(frontNotification)
        ? Date.now() + VANISHING_POPUP_DURATION_MS
        : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontNotification?.id]);

  useEffect(() => {
    const sweep = setInterval(() => {
      expireFrontRef.current();
    }, VANISH_SWEEP_INTERVAL_MS);
    return () => {
      clearInterval(sweep);
    };
  }, []);

  const handleDismissScreen = (interviewId: number) => {
    setDismissedInterviewIds((prev) => new Set(prev).add(interviewId));
  };

  const handleDismissAll = () => {
    mixpanelUtils.trackButtonClick("Dismiss all", MIXPANEL_SCREENS.DASHBOARD, {
      notification_count: notifications.length,
      screen_count: screens.length,
    });

    if (notifications.length) {
      updateNotifications({
        notificationIds: notifications.map(({ id }) => id),
        dismiss: true,
      });
    }
    if (screens.length) {
      setDismissedInterviewIds(
        (prev) =>
          new Set([...prev, ...screens.map(({ interviewId }) => interviewId)]),
      );
    }
  };

  const handleOverflowClick = () => {
    mixpanelUtils.trackButtonClick(
      "Corner prompt overflow",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        overflow_count: overflowCount,
        surface: MIXPANEL_NOTIFICATION_SURFACES.POPUP,
      },
    );
    setRotation((prev) => prev + 1);
  };

  /* Render */
  const renderOverflowBadge = () => {
    if (overflowCount < 1) return null;

    return (
      <Badge
        className="pointer-events-auto absolute -left-2 -top-2 h-6 min-w-6 cursor-pointer justify-center rounded-full border-2 border-white bg-primary-600 px-1.5 text-xs font-semibold leading-none text-white shadow-md transition-colors hover:bg-primary-700"
        onClick={handleOverflowClick}
      >
        +{overflowCount}
      </Badge>
    );
  };

  const render = () => {
    if (!frontNotification && !frontScreen && !frontPauseRequest) return null;

    return (
      <Column
        className={cn(
          "pointer-events-none fixed bottom-2 left-2 z-[60]",
          CORNER_POPUP_WIDTH_CLASS,
        )}
        data-notification-popup // Add global identifier so CandidateSheet can exempt it from its outside-click close, matching data-screening-card
      >
        <Row className="relative">
          {frontNotification ? (
            <Row
              className="w-full"
              onMouseEnter={() => {
                hoveredNotificationIdRef.current = frontNotification.id;
              }}
              onMouseLeave={() => {
                hoveredNotificationIdRef.current = null;
              }}
            >
              <NotificationPopupCard
                isExiting={dismissingNotificationId === frontNotification.id}
                key={frontNotification.id}
                notification={frontNotification}
                onDismiss={handleDismissNotification}
                onDismissAll={canDismissAll ? handleDismissAll : undefined}
              />
            </Row>
          ) : null}
          {frontPauseRequest ? (
            <PauseRequestCard
              key={frontPauseRequest.requestId}
              pauseRequest={frontPauseRequest}
            />
          ) : null}
          {frontScreen ? (
            <ScreeningInProgressContainer
              activeScreen={frontScreen}
              key={frontScreen.interviewId}
              onDismiss={() => {
                handleDismissScreen(frontScreen.interviewId);
              }}
              onDismissAll={canDismissAll ? handleDismissAll : undefined}
            />
          ) : null}
          {renderOverflowBadge()}
        </Row>
      </Column>
    );
  };

  return render();
};
